import { httpClient } from "@/shared/api";

import type { Master, MasterInput } from "../model/types";
import type {
  CreateMasterRequestDTO,
  ListMastersResponseDTO,
  MasterResponseDTO,
  PhotoUploadResponseDTO,
} from "./dto";

/** Опції upload-у фото (§4.1): прогрес-колбек + signal для скасування. */
export interface UploadOpts {
  onProgress?: (percent: number | "indeterminate") => void;
  signal?: AbortSignal;
}

/** DTO (snake_case) → доменний `Master` (camelCase). Єдина точка мапінгу. */
function toDomain(dto: MasterResponseDTO): Master {
  return {
    id: dto.id,
    firstName: dto.first_name,
    lastName: dto.last_name,
    middleName: dto.middle_name,
    email: dto.email,
    phone: dto.phone,
    photoUrl: dto.photo_url,
    serviceIds: dto.service_ids,
  };
}

/**
 * Білдер тіла запиту (§7.3), спільний для create і update (PATCH = повна
 * заміна). Обовʼязкові `first_name`/`last_name` — завжди (тримлені). Опційні
 * `middle_name`/`email`/`phone` **пропускаються**, коли порожні після тримінгу:
 * для PATCH «omitted = cleared» (бекенд очищує), для POST — створення без них.
 * Не шлемо `""` — обходить ризик `format:email` на порожньому рядку.
 */
export function buildMasterRequestBody(
  input: MasterInput,
): CreateMasterRequestDTO {
  const body: CreateMasterRequestDTO = {
    first_name: input.firstName.trim(),
    last_name: input.lastName.trim(),
  };
  const middleName = input.middleName.trim();
  const email = input.email.trim();
  const phone = input.phone.trim();
  if (middleName) {
    body.middle_name = middleName;
  }
  if (email) {
    body.email = email;
  }
  if (phone) {
    body.phone = phone;
  }
  return body;
}

/**
 * `GET /api/masters` — усі майстри компанії сесії (RLS scoping серверно).
 * 401 ловить глобальний interceptor (ADR-0006 D4); reject уже нормалізований
 * у `AppError` (ADR-0010 D4).
 */
export async function listMasters(): Promise<Master[]> {
  const { data } = await httpClient.get<ListMastersResponseDTO>("/masters");
  return data.masters.map(toDomain);
}

/** `GET /api/masters/{id}` — один майстер; 200 → доменний `Master`. */
export async function getMaster(id: string): Promise<Master> {
  const { data } = await httpClient.get<MasterResponseDTO>(`/masters/${id}`);
  return toDomain(data);
}

/** `POST /api/masters` (CSRF) — створює майстра; 201 → доменний `Master`. */
export async function createMaster(input: MasterInput): Promise<Master> {
  const body = buildMasterRequestBody(input);
  const { data } = await httpClient.post<MasterResponseDTO>("/masters", body);
  return toDomain(data);
}

/**
 * `PATCH /api/masters/{id}` (CSRF) — **повна заміна** профілю; 200 → доменний
 * `Master`. Тіло — повний профіль (§7.3); пропущені опц. поля бекенд очищує.
 */
export async function updateMaster(
  id: string,
  input: MasterInput,
): Promise<Master> {
  const body = buildMasterRequestBody(input);
  const { data } = await httpClient.patch<MasterResponseDTO>(
    `/masters/${id}`,
    body,
  );
  return toDomain(data);
}

/** `DELETE /api/masters/{id}` (CSRF) — hard-delete; 204 без тіла. */
export async function deleteMaster(id: string): Promise<void> {
  await httpClient.delete(`/masters/${id}`);
}

/**
 * `POST /api/masters/{id}/photo` (multipart, частина `photo`; CSRF) — завантажує/
 * замінює фото; 200 → новий абсолютний `photo_url` (§7.2/§7.3). **Content-Type не
 * ставимо вручну** — axios сам виставить `multipart/form-data; boundary=…` з
 * FormData. `onUploadProgress` мапиться у percent (`total` відомий) або
 * `'indeterminate'`. `signal` (AbortController) → скасування (§7.4). 401/
 * нормалізація — глобальний interceptor (ADR-0006 D4 / ADR-0010 D4).
 */
export async function uploadMasterPhoto(
  id: string,
  file: File,
  opts?: UploadOpts,
): Promise<string> {
  const form = new FormData();
  form.append("photo", file);
  const { data } = await httpClient.post<PhotoUploadResponseDTO>(
    `/masters/${id}/photo`,
    form,
    {
      signal: opts?.signal,
      onUploadProgress: (e) => {
        opts?.onProgress?.(
          e.total ? Math.round((e.loaded / e.total) * 100) : "indeterminate",
        );
      },
    },
  );
  return data.photo_url;
}

/** `DELETE /api/masters/{id}/photo` (CSRF) — idempotent; 204 без тіла. */
export async function deleteMasterPhoto(id: string): Promise<void> {
  await httpClient.delete(`/masters/${id}/photo`);
}

